/**
 * Session context: bootstraps from `/api/auth/me`, exposes login/logout —
 * and owns session-expiry detection.
 *
 * Two mechanisms keep an idle tab honest instead of silently frozen:
 * the API client's global 401 hook (any request after expiry — including
 * the dashboard feed's auth-refused close — lands here and flips the app
 * to an explicit "session expired" state), and a visibility recheck that
 * pings `/api/auth/me` when the tab regains focus, so the user learns
 * immediately rather than on their next click.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  getCurrentSession,
  loginLocal,
  logout as apiLogout,
  setCsrfToken,
  setUnauthenticatedHandler,
} from "@pbxmgr/api";
import type { SessionUser } from "@pbxmgr/api";

/** Re-check no more often than this when the tab regains focus. */
const FOCUS_RECHECK_MS = 15_000;

interface AuthContextValue {
  session: SessionUser | null;
  loading: boolean;
  /** True when a live session expired out from under the user. */
  expired: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Provides the current session and auth actions to the app. */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  // Refs so the stable 401 handler / focus listener see current state.
  const sessionRef = useRef<SessionUser | null>(null);
  sessionRef.current = session;
  const lastCheckRef = useRef(0);

  useEffect(() => {
    let active = true;
    getCurrentSession()
      .then((s) => {
        if (active) setSession(s);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Any 401 anywhere = the session died. Only treat it as *expiry* when a
  // session actually existed — the login form's own 401s and the anonymous
  // bootstrap probe must not trigger the notice.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      if (sessionRef.current !== null) {
        setSession(null);
        setExpired(true);
        setCsrfToken(null);
      }
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  // Returning to an idle tab: verify the session right away (debounced).
  // On expiry the /me 401 flows through the handler above; on success the
  // refreshed session (and permissions) replace the stale one.
  useEffect(() => {
    const recheck = (): void => {
      if (document.visibilityState !== "visible") return;
      if (sessionRef.current === null) return;
      const now = Date.now();
      if (now - lastCheckRef.current < FOCUS_RECHECK_MS) return;
      lastCheckRef.current = now;
      void getCurrentSession().then((s) => {
        if (s) setSession(s);
      });
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setSession(await loginLocal(username, password));
    setExpired(false);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setSession(null);
    setExpired(false);
  }, []);

  return (
    <AuthContext.Provider value={{ session, loading, expired, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Access the auth context; throws if used outside the provider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
